/**
 * Discipline framework types — BAB-60.
 *
 * Mirrors the backend's `DisciplineState` Pydantic schema (see
 * `backend/app/schemas/portfolio.py`). Single source of truth for the
 * 3-way intent enum + the engine output shape consumed by the position
 * card's State + reasoning surfaces.
 */

export type Intent = 'hold' | 'wheel' | 'release';

export type DisciplineAction =
  | 'Hold'
  | 'Close'
  | 'Close at Loss'
  | 'Close Both'
  | 'Roll'
  | 'Let Assign'
  | 'Close at Debit'
  | 'Stale Data'
  | 'Insufficient Data';

export type DisciplineTrigger =
  | 'thesis_broken'
  | 'stock_stop'
  | 'option_stop'
  | 'structure_stop'
  | 'profit_target'
  | 'time_stop'
  | 'delta_breach'
  | 'earnings'      // v3.1 event risk
  | 'ex_dividend'   // v3.1 event risk (early assignment)
  | 'hold'
  | 'stale_data'
  | 'insufficient_data';

/** The rule that fired the current verdict. Always-visible "Rule: ..." line. */
export interface DisciplineFiredRule {
  name: string;
  formula_human: string;
  observed_value: number;
  threshold: number;
  direction: string;
}

/** The single closest condition that would change the verdict. */
export interface DisciplineFlipCondition {
  summary: string;
  would_become: DisciplineAction;
}

export interface DisciplineDerivedValues {
  profit_pct: number | null;
  dte_remaining: number | null;
  abs_delta: number | null;
  stock_drop_pct: number | null;
  option_mark_ratio: number | null;
  cost_basis_breach: boolean | null;
}

export interface DisciplineRollTarget {
  new_strike: number;
  new_expiry: string;
  expected_credit: number;
  cost_basis_check: boolean;
  new_delta: number;
  dte_extension: number;
  /** Per-share quote context for the order line; absent on older payloads. */
  new_price?: number | null;
  new_bid?: number | null;
  new_ask?: number | null;
  buyback_mark?: number | null;
  /** The roll expires on/after the next earnings report (the engine only picks
   *  one when no viable roll expires before it). */
  crosses_earnings?: boolean;
}

/**
 * One row of the card's "Your rules" list: a rule the engine actually
 * evaluates for this position (intent × type). `name` is identical to the
 * badge's fired_rule.name by construction; the *_display strings are
 * backend-formatted in badge-copy units — render verbatim, never reformat.
 */
export interface DisciplineRuleStatus {
  key: string;
  name: string;
  observed: number | null;
  threshold: number;
  observed_display: string;
  threshold_display: string;
  fired: boolean;
  is_nearest: boolean;
  /** Gold v2 phase 3b (glossary rule_line), computed by the engine: the stock
   *  price at which the rule fires today (null for time rules or without IV). */
  line_price?: number | null;
  /** ISO New York date the rule fires if price and IV stay put (time stop = capped date). */
  trigger_date?: string | null;
  /** The line over time to expiry (profit target, defensive delta) — the chart draws it. */
  line_curve?: Array<{ date: string; price: number }> | null;
}

/**
 * BAB-6 — Option A, take assignment. CC-only (a CSP assignment deploys
 * capital, not frees it). All dollars APPROXIMATE (API-only marks).
 */
export interface DisciplineAssignmentEV {
  realized_pnl: number;
  capital_freed: number;
  annualized_return_pct: number | null;
  days_held: number | null;
}

/**
 * BAB-6 — Option B, the engine's chosen roll target, costed. The wedge is
 * `locked_capital_yield_pct`: annualized yield on the (strike × shares)
 * capital the roll keeps tied up instead of freeing via assignment.
 */
export interface DisciplineRollEconomics {
  net_credit: number;
  new_max_profit: number | null;
  locked_capital: number;
  locked_capital_yield_pct: number | null;
  post_roll_delta: number | null;
  dte_extension: number | null;
}

/** BAB-6 — a roll that breaks the user's own rule (or is an arithmetic loss). */
export interface DisciplineRollTrap {
  flag: string;
  severity: 'alert' | 'warning';
  message: string;
}

/** BAB-6 — roll-vs-assign side-by-side economics for the ResolveModal decide step. */
export interface DisciplineDecision {
  assignment: DisciplineAssignmentEV | null;
  roll: DisciplineRollEconomics | null;
  traps: DisciplineRollTrap[];
  is_approximate: boolean;
}

/** Full engine output. Returned by GET /positions/{id}/discipline. */
export interface DisciplineState {
  action: DisciplineAction;
  trigger: DisciplineTrigger;
  engine_version: string;
  fired_rule: DisciplineFiredRule | null;
  flip_condition: DisciplineFlipCondition | null;
  derived_values: DisciplineDerivedValues;
  roll_target: DisciplineRollTarget | null;
  expected_loss: number | null;
  notes: string[];
  /** Optional: absent on older cached payloads — hide the rules section then. */
  rules?: DisciplineRuleStatus[];
  /** BAB-6: roll-vs-assign economics. Absent/null on older payloads or when N/A. */
  decision?: DisciplineDecision | null;
  /** Gold v2 phase 3b: the basis the engine compared against (CC: gold net basis; put: strike). */
  net_basis?: number | null;
  basis_source?: string | null;
}

/**
 * 5-color action palette (per the product-designer review of the spec).
 * Hold=grey, Close=green, Roll=blue, Let Assign=indigo, all "Close at *"=red.
 * Data-quality actions get muted greys.
 */
export const ACTION_COLORS: Record<DisciplineAction, string> = {
  'Hold':              '#9CA3AF',  // grey — status quo
  'Close':             '#10B981',  // green — clean win
  'Roll':              '#3B82F6',  // blue — continuation
  'Let Assign':        '#6366F1',  // indigo — wheel outcome
  'Close at Loss':     '#EF4444',  // red — taking the L
  'Close at Debit':    '#EF4444',  // red — defended at cost
  'Close Both':        '#EF4444',  // red — thesis broken, exit
  'Stale Data':        '#6B7280',  // mute
  'Insufficient Data': '#6B7280',  // mute
};

/** School labels for the 3-way intent chip. */
export const INTENT_LABELS: Record<Intent, string> = {
  hold:    'Keep My Shares',
  wheel:   'Cycle for Income',
  release: 'Harvest Premium',
};

/**
 * Position-type-aware intent label. The conservative 'hold' stance reads
 * "Keep My Shares" for a covered call (you own shares; defend them) but
 * "Stay in Cash" for a cash-secured put (you own no shares; the conservative
 * move is to avoid being assigned). 'wheel' and 'release' are identical for
 * both. Pass `isPut=true` for a CSP/put context.
 */
export const intentLabel = (intent: Intent, isPut: boolean): string =>
  intent === 'hold' && isPut ? 'Stay in Cash' : INTENT_LABELS[intent];

/** The school each intent maps to + citation source for the provenance UI. */
export const INTENT_SCHOOLS: Record<Intent, { school: string; stance: string }> = {
  hold:    { school: 'BCI (Alan Ellman)',           stance: 'long-term holding; assignment is unwanted, defend' },
  wheel:   { school: 'r/thetagang Wheel',           stance: 'cycle through CSP→assignment→CC; assignment is the goal' },
  release: { school: 'tastytrade / Option Alpha',   stance: 'premium harvesting; close before assignment' },
};

// ============================================================================
// BAB-61: Threshold Config types (Settings → Frameworks → 3 Discipline tabs)
// ============================================================================

/** One intent's six thresholds. Mirrors backend DisciplineThresholdsRead. */
export interface DisciplineThresholds {
  intent: Intent;
  profit_target_pct: number;
  time_stop_dte: number;
  defensive_delta: number;
  stock_stop_pct: number;
  option_stop_multiple: number;
  max_roll_dte_extension: number;
  is_user_override: boolean;
  /** Glossary discipline_threshold: this intent's own default lines (server,
   *  engine.default_thresholds) — what the Settings "Spec default" tag compares to. */
  defaults?: Partial<Record<ThresholdKey, number>> | null;
}

export type ThresholdKey = 'profit_target_pct' | 'time_stop_dte' | 'defensive_delta' | 'stock_stop_pct'
  | 'option_stop_multiple' | 'max_roll_dte_extension';

/** All three intents in one payload. GET /discipline-thresholds returns this. */
export interface DisciplineThresholdsBundle {
  hold: DisciplineThresholds;
  wheel: DisciplineThresholds;
  release: DisciplineThresholds;
}

/** Update body for PUT /discipline-thresholds/{intent}. */
export interface DisciplineThresholdsUpdate {
  profit_target_pct: number;
  time_stop_dte: number;
  defensive_delta: number;
  stock_stop_pct: number;
  option_stop_multiple: number;
  max_roll_dte_extension: number;
}

/**
 * Per-control rails from spec §2.1. `hard` blocks input outside the range;
 * `soft` allows but flags inline (e.g. "Profit targets above 75% historically
 * lowered risk-adjusted returns").
 */
export interface ThresholdSpec {
  key: ThresholdKey;
  label: string;
  unit: string;
  step: number;
  hard: [number, number];
  soft?: [number, number];
  citation: string;
  helpText?: string;
}

export const THRESHOLD_SPECS: ThresholdSpec[] = [
  {
    key: 'profit_target_pct',
    label: 'Profit target',
    unit: '%',
    step: 1,
    hard: [25, 80],
    soft: [40, 60],
    citation: 'tastytrade Market Measures · Option Alpha GTC convention',
    helpText: 'Close when this % of credit has been captured.',
  },
  {
    key: 'time_stop_dte',
    label: 'Time stop',
    unit: 'DTE',
    step: 1,
    hard: [0, 30],
    soft: [14, 25],
    citation: 'tastytrade gamma-management rule · Option Alpha',
    helpText: 'Act when days to expiry drop to this line, capped at half the trade\'s original length so it never fires on entry. 0 = off (default for Keep My Shares and Cycle for Income).',
  },
  {
    key: 'defensive_delta',
    label: 'Defensive delta',
    unit: 'Δ',
    step: 0.05,
    hard: [0.10, 0.90],
    soft: [0.40, 0.85],
    citation: 'tastytrade CC rolling research',
    helpText: 'Defend when |delta| reaches this. Higher = defend later.',
  },
  {
    key: 'stock_stop_pct',
    label: 'Stock stop',
    unit: '% drop',
    step: 1,
    hard: [0, 20],
    soft: [5, 12],
    citation: 'BCI discipline synthesis',
    helpText: 'Harvest covered calls only: close both legs when the stock drops this % from the write price. 0 = off. Keep My Shares never sells your shares.',
  },
  {
    key: 'option_stop_multiple',
    label: 'Option stop',
    unit: '× credit',
    step: 0.1,
    hard: [1.5, 4.0],
    soft: [2.0, 3.0],
    citation: 'Option Alpha hard stop · tastytrade credit-multiple stops',
    helpText: 'Close when option mark reaches this × original credit. (CSP only.)',
  },
  {
    key: 'max_roll_dte_extension',
    label: 'Max roll extension',
    unit: 'days',
    step: 5,
    hard: [30, 120],
    citation: 'Framework synthesis',
    helpText: 'Reject roll candidates that extend expiry beyond this many days.',
  },
];

/**
 * Per-intent activation matrix from spec §2.1.
 * 'on'    → always rendered
 * 'off'   → hidden (not applicable to this intent × strategy combo)
 * 'opt-in' → rendered as disabled with toggle to enable
 */
export type ControlActivation = 'on' | 'off' | 'opt-in';

export interface ActivationMatrix {
  [intent: string]: {
    [strategy: string]: Partial<Record<ThresholdSpec['key'], ControlActivation>>;
  };
}

// ============================================================================
// BAB-64: Discipline preview-grid (decision-map visual)
// ============================================================================

/** Request body for POST /portfolio/discipline/preview-grid. */
export interface DisciplinePreviewGridRequest {
  intent: Intent;
  strategy: 'cc' | 'csp';
  thresholds: DisciplineThresholdsUpdate;
  stock_steps?: number;
  dte_steps?: number;
}

/** One cell of the decision-map. `action` drives color; `trigger` drives tooltip. */
export interface DisciplineGridCell {
  action: DisciplineAction;
  trigger: DisciplineTrigger;
}

/** The worked-example position the grid is computed against. */
export interface DisciplineGridExample {
  label: string;
  write_or_spot: number;
  strike: number;
  credit: number;
  entry_dte: number;
  iv: number;
}

/**
 * 2D decision-map response. `grid[i][j]` = cell at `stock_axis[i]` (% move
 * from write/spot) × `dte_axis[j]` (days remaining). FE renders as a
 * heatmap with stock on Y and DTE on X.
 */
export interface DisciplinePreviewGridResponse {
  example: DisciplineGridExample;
  stock_axis: number[];
  dte_axis: number[];
  grid: DisciplineGridCell[][];
}

/**
 * Settings-screen display only. The position card's rules list does NOT use
 * this — it renders `discipline.rules`, which the backend engine builds from
 * its own gates (single source of truth; this copy doesn't know about the
 * monied-CC structure stop and treats some cells as opt-in that V1 ships off).
 */
export const ACTIVATION_MATRIX: ActivationMatrix = {
  // v3.1 (DISCIPLINE_V3_1_SPEC): Keep never sells shares (no stock stop);
  // a put's "Stay in Cash" is evaluated as Harvest; Harvest CCs roll for credit
  // and carry the combined-loss stop (option_stop_multiple × credit).
  hold: {
    cc:  { profit_target_pct: 'on', time_stop_dte: 'opt-in', defensive_delta: 'on', stock_stop_pct: 'off', option_stop_multiple: 'off', max_roll_dte_extension: 'on' },
    csp: { profit_target_pct: 'on', time_stop_dte: 'on',     defensive_delta: 'on', stock_stop_pct: 'off', option_stop_multiple: 'on',  max_roll_dte_extension: 'on' },
  },
  wheel: {
    cc:  { profit_target_pct: 'on', time_stop_dte: 'opt-in', defensive_delta: 'on', stock_stop_pct: 'off', option_stop_multiple: 'off', max_roll_dte_extension: 'on' },
    csp: { profit_target_pct: 'on', time_stop_dte: 'opt-in', defensive_delta: 'on', stock_stop_pct: 'off', option_stop_multiple: 'off', max_roll_dte_extension: 'on' },
  },
  release: {
    cc:  { profit_target_pct: 'on', time_stop_dte: 'on', defensive_delta: 'on', stock_stop_pct: 'opt-in', option_stop_multiple: 'on', max_roll_dte_extension: 'on' },
    csp: { profit_target_pct: 'on', time_stop_dte: 'on', defensive_delta: 'on', stock_stop_pct: 'off',    option_stop_multiple: 'on', max_roll_dte_extension: 'on' },
  },
};

/** The lines (thresholds) one intent applies. */
export interface DisciplineIntentLines {
  profit_target_pct: number;
  time_stop_dte: number;
  defensive_delta: number;
  stock_stop_pct: number;
  option_stop_multiple: number;
  max_roll_dte_extension: number;
}

/** GET /positions/{id}/discipline/intents — the verdict under each intent, read-only. */
export interface DisciplineByIntent {
  current_intent: Intent;
  verdicts: Record<Intent, DisciplineState & { thresholds: DisciplineIntentLines }>;
}
